import { useState, useEffect } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { Badge } from '../../../components/ui/badge'
import { Input } from '../../../components/ui/input'
import { Button } from '../../../components/ui/button'
import { useSidebar } from '../../../components/ui/sidebar'

interface Project {
  id: number
  name: string
}

interface DevInstance {
  id: number
  name: string
  status: string
  runtime_state: string
  ip_address: string
  instance_id: string
  created_at: string
  infrastructure_type: string
  pilot_version_display?: string
  target_pilot_version?: string | null
  project_type: 'rails_app' | 'django_app' | 'managed_agent'
  django_stack: boolean
  is_demo: boolean
  source_repo_name?: string
  starter_base_version_display?: string
  target_starter_base_version?: string | null
  project?: Project
}

interface Pagination {
  page: number
  count: number
  pages: number
  last: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface AdminDevInstancesIndexProps {
  dev_instances: DevInstance[]
  pagy: Pagination
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function Index({ dev_instances, pagy }: AdminDevInstancesIndexProps) {
  const { t } = useTranslation('admin')
  const { state: sidebarState } = useSidebar()
  const [searchName, setSearchName] = useState('')
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false)
  const [hideDemos, setHideDemos] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [bulkVersion, setBulkVersion] = useState('')
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const [bulkStarterBaseVersion, setBulkStarterBaseVersion] = useState('')
  const [bulkStarterBaseUpdating, setBulkStarterBaseUpdating] = useState(false)

  // Initialize filter state from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const nameFilter = params.get('q[name_cont]')
    const projectIdNull = params.get('q[project_id_null]')
    const isDemoFalse = params.get('q[is_demo_eq]')

    if (nameFilter) {
      setSearchName(nameFilter)
    }
    if (projectIdNull === '1') {
      setShowUnassignedOnly(true)
    }
    if (isDemoFalse === 'false') {
      setHideDemos(true)
    }
  }, [])

  const handleSearch = () => {
    const params: Record<string, string> = {}

    if (searchName) params['q[name_cont]'] = searchName
    if (showUnassignedOnly) params['q[project_id_null]'] = '1'
    if (hideDemos) params['q[is_demo_eq]'] = 'false'

    router.get('/admin/dev_instances', params, { preserveState: true })
  }

  const handleReset = () => {
    setSearchName('')
    setShowUnassignedOnly(false)
    setHideDemos(false)
    router.get('/admin/dev_instances', {}, { preserveState: true })
  }

  const handleSort = (field: string) => {
    const params = new URLSearchParams(window.location.search)
    const currentSort = params.get('q[s]')

    // Toggle between asc and desc
    let newSort = `${field} asc`
    if (currentSort === `${field} asc`) {
      newSort = `${field} desc`
    }

    const searchParams: Record<string, string> = {}

    // Preserve existing search filters
    if (searchName) searchParams['q[name_cont]'] = searchName
    if (showUnassignedOnly) searchParams['q[project_id_null]'] = '1'

    // Add sort parameter
    searchParams['q[s]'] = newSort

    router.get('/admin/dev_instances', searchParams, { preserveState: true })
  }

  const getSortIcon = (field: string) => {
    const params = new URLSearchParams(window.location.search)
    const currentSort = params.get('q[s]')

    if (currentSort === `${field} asc`) {
      return '↑'
    } else if (currentSort === `${field} desc`) {
      return '↓'
    }
    return '⇅'
  }

  const getStatusBadge = (status: string) => {
    const statusVariants: Record<string, "success" | "warning" | "info" | "destructive" | "secondary"> = {
      'active': 'success',
      'setup_in_progress': 'warning',
      'provisioning': 'info',
      'failed': 'destructive',
      'terminated': 'secondary',
      'archive': 'secondary',
      'setup_failed': 'destructive',
    }

    return (
      <Badge variant={statusVariants[status] || 'secondary'} className="text-xs px-1.5 py-0.5">
        {t(`devInstances.status.${status}`, { defaultValue: status.replace('_', ' ') })}
      </Badge>
    )
  }

  const getRuntimeStateBadge = (runtimeState: string) => {
    const runtimeStateVariants: Record<string, "success" | "secondary"> = {
      'running': 'success',
      'stopped': 'secondary',
    }

    return (
      <Badge variant={runtimeStateVariants[runtimeState] || 'secondary'} className="text-xs px-1.5 py-0.5">
        {t(`devInstances.runtimeState.${runtimeState}`, { defaultValue: runtimeState })}
      </Badge>
    )
  }

  const getTemplateLabel = (sourceRepo?: string) => {
    if (!sourceRepo) return null
    if (sourceRepo.includes('saas')) return 'SaaS'
    if (sourceRepo.includes('crm')) return 'CRM'
    if (sourceRepo.includes('starter-base')) return 'Base'
    return sourceRepo.replace('django-', '').replace('-base', '')
  }

  const handleToggleSelect = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id))
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }

  const handleSelectAll = () => {
    if (selectedIds.length === dev_instances.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(dev_instances.map(instance => instance.id))
    }
  }

  const handleBulkVersionUpdate = (
    endpoint: string,
    version: string,
    setUpdating: (v: boolean) => void,
    setVersion: (v: string) => void,
    confirmKey: string
  ) => {
    if (selectedIds.length === 0) {
      alert(t('devInstances.index.bulkActions.selectError'))
      return
    }
    if (!version) {
      alert(t('devInstances.index.bulkActions.versionError'))
      return
    }
    if (!confirm(t(confirmKey, { count: selectedIds.length, version }))) {
      return
    }
    setUpdating(true)
    router.post(endpoint, {
      dev_instance_ids: selectedIds,
      target_version: version
    }, {
      onFinish: () => {
        setUpdating(false)
        setSelectedIds([])
        setVersion('')
      }
    })
  }

  const buildParams = () => {
    const params: Record<string, string> = {}
    if (searchName) params['q[name_cont]'] = searchName
    if (showUnassignedOnly) params['q[project_id_null]'] = '1'
    if (hideDemos) params['q[is_demo_eq]'] = 'false'
    return params
  }

  return (
    <>
      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col py-2">
            <div className="px-3">
              {/* Compact Filter Bar */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold whitespace-nowrap">
                  {t('devInstances.index.titleWithCount', { count: pagy.count })}
                </span>
                <div className="w-px h-4 bg-border" />
                <Input
                  type="text"
                  placeholder={t('devInstances.index.filters.namePlaceholder')}
                  className="h-7 text-xs w-40"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <label className="flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showUnassignedOnly}
                    onChange={(e) => setShowUnassignedOnly(e.target.checked)}
                    className="h-3 w-3 rounded border-gray-300"
                  />
                  {t('devInstances.index.filters.unassigned')}
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={hideDemos}
                    onChange={(e) => setHideDemos(e.target.checked)}
                    className="h-3 w-3 rounded border-gray-300"
                  />
                  {t('devInstances.index.filters.hideDemos')}
                </label>
                <Button onClick={handleSearch} className="h-7 px-2 text-xs">
                  {t('devInstances.index.buttons.search')}
                </Button>
                <Button onClick={handleReset} variant="ghost" className="h-7 px-2 text-xs">
                  {t('devInstances.index.buttons.reset')}
                </Button>
                <div className="flex-1" />
                <Button onClick={() => router.visit('/admin/dev_instances/new')} className="h-7 px-2 text-xs">
                  {t('devInstances.index.createButton')}
                </Button>
              </div>

              {/* Dev Instances Table - Single Flat Table */}
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px)', minHeight: '400px' }}>
                  <table className="w-full">
                    <thead className="bg-muted sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-1.5 text-left">
                          <input
                            type="checkbox"
                            checked={selectedIds.length === dev_instances.length && dev_instances.length > 0}
                            onChange={handleSelectAll}
                            className="h-3 w-3 rounded border-gray-300"
                          />
                        </th>
                        <th
                          className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center gap-1">
                            {t('devInstances.index.table.name')}
                            <span className="text-gray-400">{getSortIcon('name')}</span>
                          </div>
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('devInstances.index.table.project')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          Stack
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          Template
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('devInstances.index.table.status')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('devInstances.index.table.runtimeState')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          IP
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          Pilot
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('devInstances.index.table.starterBase')}
                        </th>
                        <th
                          className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                          onClick={() => handleSort('created_at')}
                        >
                          <div className="flex items-center gap-1">
                            {t('devInstances.index.table.created')}
                            <span className="text-gray-400">{getSortIcon('created_at')}</span>
                          </div>
                        </th>
                        <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                          {t('devInstances.index.table.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {dev_instances.length > 0 ? (
                        dev_instances.map((instance) => (
                          <tr key={instance.id} className={`hover:bg-muted transition-colors ${instance.is_demo ? 'bg-blue-100 dark:bg-blue-900/40' : ''}`}>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(instance.id)}
                                onChange={() => handleToggleSelect(instance.id)}
                                className="h-3 w-3 rounded border-gray-300"
                              />
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm font-medium text-foreground">{instance.name}</span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground">
                                {instance.project?.name || (
                                  instance.is_demo ? (
                                    <Badge className="bg-blue-500 text-white text-xs px-1.5 py-0">
                                      {t('devInstances.show.templateDemo')}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )
                                )}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <Badge
                                variant={instance.project_type === 'django_app' ? 'success' : 'secondary'}
                                className="text-xs px-1.5 py-0.5"
                              >
                                {instance.project_type === 'managed_agent' ? 'Managed Agent' : instance.project_type === 'django_app' ? 'Django' : 'Rails'}
                              </Badge>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              {instance.source_repo_name ? (
                                <Badge variant="outline" className="text-xs px-1.5 py-0">
                                  {getTemplateLabel(instance.source_repo_name)}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              {getStatusBadge(instance.status)}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              {getRuntimeStateBadge(instance.runtime_state)}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground font-mono">
                                {instance.ip_address || '-'}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className={`text-sm font-mono ${instance.target_pilot_version && instance.target_pilot_version !== instance.pilot_version_display ? 'text-amber-600' : 'text-foreground'}`}>
                                {instance.pilot_version_display || '-'}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className={`text-sm font-mono ${instance.target_starter_base_version && instance.target_starter_base_version !== instance.starter_base_version_display ? 'text-amber-600' : 'text-foreground'}`}>
                                {instance.starter_base_version_display || '-'}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground">
                                {new Date(instance.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end gap-1">
                                {instance.status === 'stopped' ? (
                                  <Button
                                    size="sm"
                                    variant="default"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => router.post(`/admin/dev_instances/${instance.id}/start`)}
                                  >
                                    {t('devInstances.index.actions.start')}
                                  </Button>
                                ) : (instance.status === 'active' || instance.status === 'setup_failed') && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => router.post(`/admin/dev_instances/${instance.id}/stop`)}
                                  >
                                    {t('devInstances.index.actions.stop')}
                                  </Button>
                                )}
                                <Link
                                  href={`/admin/dev_instances/${instance.id}`}
                                  className="text-sm text-blue-600 hover:text-blue-900 px-1"
                                >
                                  {t('devInstances.index.table.view')}
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={12} className="px-3 py-8 text-center text-sm text-muted-foreground">
                            {t('devInstances.index.empty')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Compact Pagination */}
                {pagy.pages > 1 && (
                  <div className="px-3 py-1.5 flex items-center justify-between border-t border-border bg-card text-xs">
                    <span className="text-muted-foreground">
                      {pagy.from}-{pagy.to} of {pagy.count}
                    </span>
                    <div className="flex gap-1">
                      {pagy.prev && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            router.get('/admin/dev_instances', { ...buildParams(), page: pagy.prev }, { preserveState: true })
                          }}
                        >
                          {t('devInstances.index.pagination.previous')}
                        </Button>
                      )}
                      {pagy.next && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            router.get('/admin/dev_instances', { ...buildParams(), page: pagy.next }, { preserveState: true })
                          }}
                        >
                          {t('devInstances.index.pagination.next')}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Update Banner - Fixed at Bottom */}
      {selectedIds.length > 0 && (
        <div
          className="fixed bottom-0 right-0 bg-blue-600 text-white shadow-lg z-[9999] transition-all duration-200"
          style={{
            left: sidebarState === 'expanded' ? '16rem' : '4rem'
          }}
        >
          <div className="px-4 lg:px-6 py-3 flex items-center justify-between gap-4 border-t border-blue-700">
            <div className="flex items-center gap-4">
              <div className="text-sm font-medium">
                {t('devInstances.index.bulkActions.selected', { count: selectedIds.length })}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder={t('devInstances.index.bulkActions.versionPlaceholder')}
                  value={bulkVersion}
                  onChange={(e) => setBulkVersion(e.target.value)}
                  className="h-8 bg-white text-gray-900 placeholder:text-gray-500 text-sm"
                  disabled={bulkUpdating}
                />
                <Button
                  onClick={() => handleBulkVersionUpdate('/admin/dev_instances/bulk_update_pilot', bulkVersion, setBulkUpdating, setBulkVersion, 'devInstances.index.bulkActions.confirmUpdate')}
                  disabled={bulkUpdating || !bulkVersion}
                  variant="secondary"
                  size="sm"
                  className="whitespace-nowrap h-8"
                >
                  {bulkUpdating ? t('devInstances.index.bulkActions.updating') : t('devInstances.index.bulkActions.updatePilot')}
                </Button>
              </div>
              <div className="w-px h-6 bg-blue-400" />
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder={t('devInstances.index.bulkActions.starterBaseVersionPlaceholder')}
                  value={bulkStarterBaseVersion}
                  onChange={(e) => setBulkStarterBaseVersion(e.target.value)}
                  className="h-8 bg-white text-gray-900 placeholder:text-gray-500 text-sm"
                  disabled={bulkStarterBaseUpdating}
                />
                <Button
                  onClick={() => handleBulkVersionUpdate('/admin/dev_instances/bulk_update_starter_base', bulkStarterBaseVersion, setBulkStarterBaseUpdating, setBulkStarterBaseVersion, 'devInstances.index.bulkActions.confirmStarterBaseUpdate')}
                  disabled={bulkStarterBaseUpdating || !bulkStarterBaseVersion}
                  variant="secondary"
                  size="sm"
                  className="whitespace-nowrap h-8"
                >
                  {bulkStarterBaseUpdating ? t('devInstances.index.bulkActions.updating') : t('devInstances.index.bulkActions.updateStarterBase')}
                </Button>
              </div>
            </div>
            <Button
              onClick={() => setSelectedIds([])}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10 whitespace-nowrap h-8"
            >
              {t('devInstances.index.bulkActions.clear')}
            </Button>
          </div>
        </div>
      )}
    </>
  )
}




Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
